import { Helmet } from "react-helmet-async";

const SITE_URL = (process.env.REACT_APP_PUBLIC_URL || window.location.origin).replace(/\/$/, "");
const DEFAULT_OG = `${SITE_URL}/og-default.png`;

/**
 * Drop-in SEO header — sets <title>, meta description, OG and Twitter cards,
 * canonical URL, and optional JSON-LD schema.org markup.
 *
 * For essays we pass `article` props (publishedAt, category, readingMin, etc.)
 * which automatically produce schema.org/Article JSON-LD.
 */
export function SeoHead({
  title,
  description,
  path = "",
  image,
  type = "website",
  article,
}) {
  const fullTitle = title?.endsWith("SugarSense") ? title : `${title || "SugarSense"} — SugarSense`;
  const url = `${SITE_URL}${path.startsWith("/") ? path : `/${path}`}`.replace(/\/$/, "") || SITE_URL;
  const ogImage = image || DEFAULT_OG;

  const articleSchema = article
    ? {
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": title,
        "description": description,
        "image": [ogImage],
        "datePublished": article.publishedAt,
        "dateModified": article.updatedAt || article.publishedAt,
        "author": { "@type": "Organization", "name": article.author || "SugarSense Editorial" },
        "publisher": {
          "@type": "Organization",
          "name": "SugarSense",
          "logo": { "@type": "ImageObject", "url": `${SITE_URL}/og-default.svg` },
        },
        "mainEntityOfPage": { "@type": "WebPage", "@id": url },
        "articleSection": article.category,
        "wordCount": article.wordCount,
        "timeRequired": article.readingMin ? `PT${article.readingMin}M` : undefined,
      }
    : null;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}
      <link rel="canonical" href={url} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="SugarSense" />
      <meta property="og:title" content={title || "SugarSense"} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:url" content={url} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title || "SugarSense"} />
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={ogImage} />
      {article?.publishedAt && <meta property="article:published_time" content={article.publishedAt} />}
      {article?.updatedAt && <meta property="article:modified_time" content={article.updatedAt} />}
      {article?.category && <meta property="article:section" content={article.category} />}
      {articleSchema && (
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      )}
    </Helmet>
  );
}
